<script setup>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import AdvancedComparisonProductResultAction from 'components/competitor-app-v2/advanced-comparisons/AdvancedComparisonProductResultAction.vue';
import FavoriteAction from 'components/product-search/FavoriteAction.vue';

const props = defineProps({
  pigment: {
    type: Object,
    required: true
  },
  forIndustry: {
    type: String,
    required: false,
    default: null
  }
});

const store = useStore();
const { t } = useI18n();

// Store getters and state
const compareListProductUids = computed(() => store.getters['productComparison/productUids']);
const basketLimitReached = computed(() => store.getters['productComparison/basketLimitReached']);
const activeComparisonIndustry = computed(() => store.getters['productComparison/activeComparisonIndustry']);
const activeComparison = computed(() => store.state.productComparison.activeComparison);
const services = computed(() => store.state.generic.services);
const activeIndustries = computed(() => store.getters.activeIndustries);
const industries = computed(() => store.state.generic.industries);

// Computed properties
const colorImage = computed(() => {
  if (activeIndustries.value?.length === 1) {
    const i = activeIndustries.value[0];

    if (props.pigment.stackPreviewImages?.[i]?.url) {
      return props.pigment.stackPreviewImages[i].url;
    }
  }

  if (props.pigment.stackPreviewImages?.generic?.url) {
    return props.pigment.stackPreviewImages.generic.url;
  }

  if (props.pigment.image_color_chip) {
    return `${services.value.documentservice.url}/api/v2/document/download/${props.pigment.image_color_chip.id}`;
  }

  if (props.pigment.color_values
      && typeof props.pigment.color_values === 'object'
      && props.pigment.color_values.hasOwnProperty('image')
      && props.pigment.color_values.image) {
    return props.pigment.color_values.image;
  }

  return null;
});

const link = computed(() => {
  if (!props.forIndustry) return props.pigment.link;

  const baseLink = props.pigment.link;
  const token = baseLink.indexOf('?') === -1 ? '?' : '&';

  return `${baseLink}${token}i=${props.forIndustry}`;
});

const isOnCompareList = computed(() => compareListProductUids.value.indexOf(props.pigment.uid) > -1);

const isSameIndustryAsComparison = computed(() => {
  if (!activeComparison.value || !activeComparison.value.industry) return true;

  return props.pigment.industries.indexOf(activeComparison.value.industry) > -1;
});

const canAddToComparison = computed(() => !isOnCompareList.value && !basketLimitReached.value && isSameIndustryAsComparison.value);

const comparisonListTooltip = computed(() => {
  if (canAddToComparison.value || isOnCompareList.value) return null;

  if (basketLimitReached.value) return t('productComparison.basket.tooltips.limitReached');

  if (!isSameIndustryAsComparison.value) {
    return t('productComparison.basket.tooltips.industryLimit', { industry: activeComparisonIndustry.value.title });
  }

  return null;
});

const highlightIndustries = computed(() => {
  if (activeIndustries.value && activeIndustries.value.length > 0) {
    return activeIndustries.value;
  }

  return industries.value.map((i) => i.code);
});

const isHighlighted = computed(() => !!highlightIndustries.value.find((industry) => (
  !!props.pigment[industry]?.is_highlighted_product
)));

const highlightDescriptions = computed(() => {
  if (!isHighlighted.value) {
    return null;
  }

  return highlightIndustries.value
    .map((industry) => {
      if (props.pigment[industry]?.is_highlighted_product && props.pigment[industry]?.product_highlight_description) {
        return {
          industry: industries.value.find((i) => i.code === industry),
          content: props.pigment[industry].product_highlight_description,
        };
      }

      return null;
    })
    .filter(Boolean);
});

// Methods
const addToComparison = () => {
  if (!canAddToComparison.value) return;

  // Check if an industry selection is necessary
  if (!activeComparison.value || !activeComparison.value.industry) {
    let industry = null;

    // Pigment has exactly one industry
    if (props.pigment.industries.length === 1) {
      [industry] = props.pigment.industries;
    } else if (props.forIndustry) {
      // Exactly one industry filter active, so use that one
      industry = props.forIndustry;
    }

    store.dispatch('productComparison/addToBasket', {
      product: props.pigment,
      industry
    });
  } else {
    store.dispatch('productComparison/addToBasket', {
      product: props.pigment
    });
  }
};

function openDetailPage() {
  location.href = link.value;
}
</script>

<template>
  <div
    class="ce-searchResult ce-searchResult-pigment"
    :class="{'ce-searchResult-pigment--highlighted': isHighlighted}"
    @click.prevent="openDetailPage">
    <div class="ce-searchResult-inner">
      <span class="ce-searchResult-colorBadgeHolder">
        <a :href="link">
          <img
            class="ce-searchResult-colorBadge"
            v-if="colorImage"
            :src="colorImage"
            :alt="pigment.product_name">
        </a>
      </span>

      <div class="flex-auto">
        <span class="ce-searchResult-pigmentName">
          <a :href="link">
            {{ pigment.product_name }}
          </a>
          <span class="ce-searchResult-internalFlag">
            <fa-icon
              v-if="pigment.isInternal"
              transform="up-6 shrink-4"
              icon="lock" />

            <fa-icon
              v-if="pigment.isActive === false"
              transform="up-6 shrink-4"
              icon="power-off" />
          </span>
        </span>

        <div
          v-if="highlightDescriptions && highlightDescriptions.length > 0"
          class="ce-searchResult-highlightDescriptions">
          <div
            v-for="(descRow, index) in highlightDescriptions"
            :key="`highlight-desc-${index}`"
            class="ce-searchResult-highlightDescription">
            <strong>{{ descRow.industry.title }}:</strong>
            {{ descRow.content }}
          </div>
        </div>
      </div>

      <div
        class="ce-searchResult-actions"
        @click.stop>
        <AdvancedComparisonProductResultAction
          :product="pigment" />

        <FavoriteAction
          :product="pigment" />

        <a
          class="ce-searchResultComparisonAction"
          :class="{disabled: !canAddToComparison}"
          href=""
          @click.prevent.stop="addToComparison">
          <tooltip
            v-if="comparisonListTooltip"
            :content="comparisonListTooltip">
            <fa-layers>
              <fa-icon icon="list" />
              <fa-icon
                v-if="!isOnCompareList"
                icon="plus-circle"
                transform="shrink-4 up-7 right-10" />
              <fa-icon
                v-else
                icon="check"
                transform="shrink-2 up-7 right-10" />
            </fa-layers>
          </tooltip>
          <fa-layers v-else>
            <fa-icon icon="list" />
            <fa-icon
              v-if="!isOnCompareList"
              icon="plus-circle"
              transform="shrink-4 up-7 right-10" />
            <fa-icon
              v-else
              icon="check"
              transform="shrink-2 up-7 right-10" />
          </fa-layers>
        </a>
      </div>
    </div>
  </div>
</template>

<style lang="less">
  @import (reference) '~styles/includes/base';

  .ce-searchResult-pigment {
    display: flex;
    align-items: center;
    border-bottom: 1px solid @color-mono-92;
    transition: all 250ms ease-in-out;
    cursor: pointer;

    .ce-searchResult-inner {
      .flex-auto;
      align-items: center;
      display: flex;
      padding: @space-3 @space-2;
    }

    a {
      text-decoration: none;
    }

    .ce-searchResult-actions {
      justify-self: flex-end;
      align-self: stretch;
      padding: 0 @space-1 0 @space-3;
      display: flex;
      align-items: center;

      > * {
        padding: 0 @space-1;
      }

      .ce-searchResultComparisonAction {
        .fa-list, .fa-plus-circle {
          color: @color-mono-60;
          transition: color 250ms ease-in-out;
        }

        .fa-check {
          color: @color-success;
        }

        &.disabled {
          cursor: default;

          .fa-plus-circle {
            color: @color-mono-60;
          }

          .fa-plus-circle, .fa-list {
            opacity: 0.3;
          }

          .fa-check {
            opacity: 0.7;
          }
        }

        &:not(.disabled):hover {
          .fa-list {
            color: @color-text-base;
          }

          .fa-plus-circle {
            color: @color-light;
          }
        }
      }

      button {
        &.disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }
      }
    }

    .ce-searchResult-pigmentName {
      a {
        color: @color-text-base;
      }
    }

    .ce-searchResult-internalFlag {
      color: @color-text-base;
    }

    .ce-searchResult-colorBadgeHolder {
      width: 10px;
      height: 20px;
      border-radius: @border-radius;
      overflow: hidden;
      margin-right: @space-2;
      flex: none;

      a {
        display: block;
        width: 100%;
        height: 100%;
      }
    }

    .ce-searchResult-colorBadge {
      width: 100%;
      height: 100%;
    }

    &:hover {
      background: @color-mono-92;
    }

    &.ce-searchResult-pigment--highlighted {
      border: 1px solid @color-sc-light-green;
      border-left-width: 6px;
      box-shadow: 2px 2px 4px fade(@color-mono-10, 20);
      margin-bottom: @space-3;
      border-radius: @border-radius;
      background: fade(@color-sc-light-green, 5);

      .ce-searchResult-colorBadgeHolder {
        width: 40px;
        height: 52px;
      }

      .ce-searchResult-pigmentName {
        font-weight: 600;
      }

      .ce-searchResult-highlightDescriptions {
        div.ce-searchResult-highlightDescription {
          margin-top: @space-1;
          white-space: pre-line;
          .ms(-2);
          line-height: 1.25;

          &:not(:last-child) {
            border-bottom: 1px solid @color-mono-80;
            padding-bottom: @space-1;
            margin-bottom: @space-1;
          }
        }
      }
    }
  }
</style>
